import { useMemo } from 'react';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';

import {
    useAssignProfile,
    useGetProfileAssignments,
} from '@/api/competency-services/profile-assignment';
import profileAssignmentService from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { queryClient } from '@/AppWithProviders';
import { CompetencyProfileQueryKey } from '@/constants/QueryKeys';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { AssignToEmployeesWizard } from '@/pages/Competencies/components/employees/AssignToEmployeesWizard';
import { getTextByLanguage } from '@/utils/localization';

type AssignProfileWizardProps = {
    profile: CompetencyProfile;
    onClose: () => void;
};

/**
 * Thin wrapper around the generic {@link AssignToEmployeesWizard}: it keeps the
 * profile-assignment wording and, on confirm, assigns the profile to the resolved
 * employees, then refreshes the profile list. The wizard shell, method handling
 * and employee resolution all live in the generic component.
 */
export const AssignProfileWizard = ({ profile, onClose }: AssignProfileWizardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const { mutateAsync: assignProfile, isPending: isAssigning } = useAssignProfile();
    const { data: currentAssignments } = useGetProfileAssignments(profile.id);

    const currentAssigneeIds = useMemo(
        () => new Set((currentAssignments ?? []).map((a) => a.employeeId)),
        [currentAssignments],
    );

    const profileName = getTextByLanguage(profile.name, language);

    return (
        <AssignToEmployeesWizard
            title={t('competencies.profiles.assign.title', { name: profileName })}
            confirmLabel={(count) => t('competencies.profiles.assign.actions.confirm', { count })}
            successMessage={(count) => t('competencies.profiles.assign.review.success', { count })}
            excludeProfileId={profile.id}
            disabledEmployeeIds={currentAssigneeIds}
            disabledEmployeeLabel={t(
                'competenciesTab.profiles.assignModal.individual.alreadyAssigned',
            )}
            isSubmitting={isAssigning}
            resolveAlreadyAssignedIds={async (employeeIds) => {
                // Employees already holding a DIFFERENT profile: assigning will replace
                // it (one profile per employee), so the review step calls it out.
                const roster = await profileAssignmentService.getRoster();
                const profileByEmployee = new Map(
                    roster.map((employee) => [employee.id, employee.assignedProfileId]),
                );

                return employeeIds.filter((employeeId) => {
                    const assignedProfileId = profileByEmployee.get(employeeId);

                    return Boolean(assignedProfileId) && assignedProfileId !== profile.id;
                });
            }}
            alreadyAssignedNote={(count) =>
                t('competencies.profiles.assign.review.alreadyAssignedNote', { count })
            }
            alreadyAssignedClause={(count) =>
                t('competencies.profiles.assign.review.alreadyAssignedClause', { count })
            }
            describeSelection={(summary) => {
                if (summary.method === 'rule') {
                    return t('competencies.profiles.assign.review.ruleDescription', {
                        count: summary.count,
                    });
                }

                if (summary.method === 'profile') {
                    return t('competencies.profiles.assign.review.profileDescription', {
                        count: summary.count,
                    });
                }

                return t('competencies.profiles.assign.review.individualDescription', {
                    count: summary.count,
                });
            }}
            onConfirm={async (employeeIds) => {
                await assignProfile({ profileId: profile.id, payload: { employeeIds } });
                void queryClient.invalidateQueries({
                    queryKey: [CompetencyProfileQueryKey.COMPETENCY_PROFILES],
                });
            }}
            onClose={onClose}
        />
    );
};
